'use client'

import { AccountCircle } from '@mui/icons-material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import MenuIcon from '@mui/icons-material/Menu'
import { SxProps, Typography } from '@mui/material'
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import CssBaseline from '@mui/material/CssBaseline'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import { styled, useTheme } from '@mui/material/styles'
import Toolbar from '@mui/material/Toolbar'
import { CSSProperties, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Outlet } from 'react-router-dom'

import { userApi } from './hooks/api/user'
import { getTitle } from './store/headerTitleSlice'
import { User } from './types/user'

import MenuListComponent from './menuList'

const drawerWidth = 160

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean
}>(({ theme, open }) => ({
  flexGrow: 1,
  marginTop: 45,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}))

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    height: 45,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  height: 45,
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}))

export default function Layout() {
  const title = useSelector(getTitle) ?? 'くもかん'
  const timeoutId = useRef<NodeJS.Timeout | null>(null) // リロードタイマーIDを保持
  const [authUser, setAuthUser] = useState<User | null>(null)

  // ユーザーが操作した際にリセット
  const resetInactivityTimer = () => {
    // リセット時の処理
    if (timeoutId.current) {
      clearTimeout(timeoutId.current)
    }
  }

  useEffect(() => {
    // ユーザー操作イベントのリスナーを設定
    const events = ['mousemove', 'keydown', 'click']
    events.forEach((event) =>
      window.addEventListener(event, resetInactivityTimer),
    )

    const fetchData = async () => {
      const response = await userApi().getMe()
      setAuthUser(response.data)
    }

    fetchData().then()

    // クリーンアップ
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current)
      }
      events.forEach((event) =>
        window.removeEventListener(event, resetInactivityTimer),
      )
    }
  }, [])
  const theme = useTheme()
  const [open, setOpen] = useState(true)

  const handleDrawerOpen = () => {
    setOpen(true)
  }

  const handleDrawerClose = () => {
    setOpen(false)
  }

  return (
    <>
      {authUser === null ? (
        <div>loading...</div>
      ) : (
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ display: 'flex', minWidth: '100vw' }}>
            <CssBaseline />
            <AppBar style={appBarStyle} open={open}>
              <Toolbar style={{ minHeight: '45px', maxHeight: '45px' }}>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  onClick={handleDrawerOpen}
                  edge="start"
                  sx={{ mr: 2, ...(open && { display: 'none' }) }}
                >
                  <MenuIcon />
                </IconButton>
                <h1 style={{ fontSize: 18, flexGrow: 2 }}>{title}</h1>
                <Typography sx={headerTextStyle}>
                  {authUser.companyBranch.company?.name}/
                  {authUser.companyBranch.branchName}
                </Typography>
                <Typography sx={headerTextStyle}>{authUser.name}</Typography>
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  color="inherit"
                >
                  <AccountCircle />
                </IconButton>
              </Toolbar>
            </AppBar>
            <Drawer
              sx={drawerMenuStyle}
              variant="persistent"
              anchor="left"
              open={open}
            >
              <DrawerHeader style={drawerHeaderStyle}>
                <IconButton onClick={handleDrawerClose}>
                  {theme.direction === 'ltr' ? (
                    <ChevronLeftIcon />
                  ) : (
                    <ChevronRightIcon />
                  )}
                </IconButton>
              </DrawerHeader>
              <Divider />
              <MenuListComponent></MenuListComponent>
            </Drawer>
            <Main open={open} style={mainStyle}>
              <Outlet />
            </Main>
          </Box>
        </Box>
      )}
    </>
  )
}

const appBarStyle: CSSProperties = {
  position: 'fixed',
}

const mainStyle: CSSProperties = {
  // コンテンツを画面いっぱいに広げる
  // 100vh - ヘッダーの高さ
  height: 'calc(100vh - 45px)',
  overflow: 'hidden',
}

const drawerMenuStyle: SxProps = {
  width: 160,
  flexShrink: 0,

  '& .MuiDrawer-paper': {
    width: 160,
    boxSizing: 'border-box',
  },
}

const drawerHeaderStyle: CSSProperties = {
  minHeight: 45,
}

const headerTextStyle: CSSProperties = {
  color: 'white',
  marginRight: '10px',
  fontSize: '14px',
}
